import { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppStore } from '../../stores/appStore';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Plus, X, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/toast';

const createAppSchema = z.object({
  organizationId: z.string().min(1, 'Organization is required'),
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less'),
  redirectUris: z.array(z.string().url('Must be a valid URL')).min(1, 'At least one redirect URI is required'),
  appUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logoUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  scope: z.array(z.string()).optional(),
});

export function CreateApplication() {
  const { createApp, loading, error, clearError, organizations, fetchOrganizations } = useAppStore();
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scopesInput, setScopesInput] = useState('');

  // Get orgId from navigation state (for super admin who selected org in list page)
  const stateOrgId = (location.state as any)?.orgId;

  const form = useForm<z.infer<typeof createAppSchema>>({
    resolver: zodResolver(createAppSchema),
    mode: 'onChange',
    defaultValues: {
      organizationId: stateOrgId || '',
      name: '',
      redirectUris: [''],
      appUrl: '',
      logoUrl: '',
      logUrl: '',
      scope: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'redirectUris' as never,
  });

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    clearError();
    if (isSuperAdmin()) {
      fetchOrganizations().catch(() => { });
    }
  }, [clearError, isSuperAdmin, fetchOrganizations]);

  // Auto-set organization for org admins
  useEffect(() => {
    if (!isSuperAdmin() && user && user.role === 'admin' && user.org_id) {
      form.setValue('organizationId', user.org_id);
    }
  }, [user, isSuperAdmin, form]);

  const handleScopesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScopesInput(e.target.value);
    const scopes = e.target.value.split(',').map(s => s.trim()).filter(s => s);
    form.setValue('scope', scopes);
  };

  const onSubmit = async (data: z.infer<typeof createAppSchema>) => {
    try {
      await createApp(data.organizationId, {
        name: data.name,
        redirectUris: data.redirectUris,
        appUrl: data.appUrl,
        logoUrl: data.logoUrl,
        logUrl: data.logUrl,
        scope: data.scope,
      });
      toast({
        title: 'Application Created',
        description: `${data.name} has been successfully created.`,
      });
      navigate('/dashboard/apps');
    } catch (error: any) {
      // Error already set in store and shown via Alert, also show toast
      toast({
        title: 'Creation Failed',
        description: error.message || 'Failed to create application',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/apps">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Applications
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Application</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Set up a new OAuth2 application</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Application Details</CardTitle>
          <CardDescription>
            Configure your OAuth2 application settings
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Creation Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Organization Selection */}
              {isSuperAdmin() ? (
                <FormField
                  control={form.control}
                  name="organizationId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an organization" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <input type="hidden" {...form.register('organizationId')} />
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="My App" {...field} />
                    </FormControl>
                    <FormDescription>
                      The display name for this application
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Redirect URIs *</FormLabel>
                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <FormField
                      control={form.control}
                      name={`redirectUris.${index}`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input placeholder="https://example.com/callback" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append('')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add URI
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  OAuth2 will redirect to these URIs after authentication
                </p>
              </div>

              <FormField
                control={form.control}
                name="appUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application URL (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="https://myapp.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Homepage URL for this application
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://myapp.com/logo.png" {...field} />
                      </FormControl>
                      <FormDescription>
                        URL to the application logo
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="logUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Log URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://myapp.com/logs" {...field} />
                      </FormControl>
                      <FormDescription>
                        Webhook URL for application logs
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel>Scopes (Optional)</FormLabel>
                <Input
                  placeholder="read, write, admin"
                  value={scopesInput}
                  onChange={handleScopesChange}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Comma-separated list of scopes (e.g., read, write, admin)
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {loading ? 'Creating...' : 'Create Application'}
                </Button>
                <Link to="/dashboard/apps">
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
